import React from 'react';
import styled from 'styled-components';

import { FourArticlesHighlightBlockDataPage } from 'api/cms';
import { Subsite } from 'api/website';
import { Box } from 'components/Box';
import { FilestackImage } from 'components/Image';
import { Link } from 'components/Link';
import { PlaceholderImage } from 'components/PlaceholderImage';
import { H4 } from 'components/Text';
import HideContainer from 'containers/HideContainer';
import { bp, gutterWidth } from 'theme';
import { trackClicked } from 'utils/analytics';
import { isBrowser } from 'utils/browser';
import { isNotEmpty } from 'utils/functional';

type Props = {
  page: FourArticlesHighlightBlockDataPage;
  qaId: string;
  displayImages: boolean;
  blockIndex: number;
  pageUpdated: string;
  subsite: Subsite | undefined;
  className?: string;
};

function UnstyledFourArticlesHighlightBlockPageSection({
  page,
  qaId,
  displayImages,
  blockIndex,
  pageUpdated,
  subsite,
  className,
}: Props) {
  const hasPlaceholder = isNotEmpty(subsite && subsite.placeholderImages);
  const hasImage = !!page.image;
  const showPlaceholder = isBrowser() && !hasImage && hasPlaceholder;
  const showImage = hasImage || showPlaceholder;

  return (
    <Box
      data-qa-id={qaId}
      width={[1, 1, 1 / 2, 1 / 4]}
      px={[gutterWidth / 4, null, null, gutterWidth / 2]}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent={['center', 'flex-start']}
      className={className}
      flex="0 0 auto"
    >
      {displayImages && (
        <Box
          position="relative"
          textAlign="center"
          mb={[gutterWidth / 4, null, gutterWidth / 2]}
        >
          {showImage ? (
            <Link
              href={`/${page.url}`}
              tabIndex={-1}
              onClick={() => {
                trackClicked('CMS Block', {
                  block_position: blockIndex,
                  block_type: 'Four Articles Highlight',
                  cta_type: 'Image',
                  page_title: page.title,
                  page_updated: pageUpdated,
                });
              }}
              aria-label={page.title}
            >
              {showPlaceholder ? (
                <PlaceholderImage
                  postId={page.id}
                  // TODO: fix types to remove need to force typecast
                  subsite={subsite as Subsite}
                  alt={page.title.slice(0, 100)}
                  width={190}
                  height={190}
                  data-qa-id="four-article-highlight-section-image"
                  round
                  fit="crop"
                  className={className}
                />
              ) : (
                <FilestackImage
                  alt={page.image?.altText || page.title.slice(0, 100)}
                  data={page.image || undefined}
                  width={190}
                  height={190}
                  data-qa-id="four-article-highlight-section-image"
                  round
                  fit="crop"
                />
              )}
            </Link>
          ) : (
            <HideContainer noWrapper xs sm>
              <div
                style={{
                  height: 190,
                }}
              />
            </HideContainer>
          )}
        </Box>
      )}
      <Link
        href={`/${page.url}`}
        onClick={() => {
          trackClicked('CMS Block', {
            block_position: blockIndex,
            block_type: 'Four Articles Highlight',
            cta_type: 'Link',
            page_title: page.title,
            page_updated: pageUpdated,
          });
        }}
      >
        <H4 mb={0} lineHeight={1.25}>
          {page.title}
        </H4>
      </Link>
    </Box>
  );
}
export const FourArticlesHighlightBlockPageSection = styled(
  UnstyledFourArticlesHighlightBlockPageSection
)`
  @media (max-width: ${bp[1] - 1}px) {
    &:not(:first-child) {
      margin-top: ${(gutterWidth * 3) / 4}px;
    }
  }
  @media (min-width: ${bp[1]}px) and (max-width: ${bp[2] - 1}px) {
    &:not(:nth-child(1)):not(:nth-child(2)) {
      margin-top: ${gutterWidth}px;
    }
  }

  a {
    position: relative;
    display: block;
    max-width: 240px;
  }
`;
